import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, Eye, ChevronLeft, ChevronRight, Loader2, Search, X, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TABLE_URL, Toast, DeleteModal, FormModal, ViewModal, DescriptionModal } from '../components/RecordFormModal';
import MailModal from '../components/MailModal';
import { getPermissions } from '../api/permissions';
import { API_BASE_URL } from '../api/config';
import { sessionManager } from '../api/sessionManager';

/* ── Status dropdown ───────────────────────────────────────────────── */
const STATUS_OPTIONS = ['Select Status', 'Hold', 'Disbursed', 'Approved', 'Rejected', 'WIP-Login', 'Pending'];

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approved' || s === 'verified') return 'bg-green-100 text-green-700 border-green-200';
  if (s === 'rejected') return 'bg-red-100 text-red-700 border-red-200';
  if (s === 'hold') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (s === 'disbursed') return 'bg-purple-100 text-purple-700 border-purple-200';
  if (s === 'wip-login' || s === 'extracted') return 'bg-blue-100 text-blue-700 border-blue-200';
  if (s === 'pending') return 'bg-orange-100 text-orange-700 border-orange-200';
  if (s === 'draft') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
}

const StatusDropdown = ({ customerId, currentStatus, onChange, employeeId, taskId }) => {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      const scrollHandler = (e) => {
        if (menuRef.current && menuRef.current.contains(e.target)) return;
        setOpen(false);
      };
      window.addEventListener('scroll', scrollHandler, true);
      return () => {
        document.removeEventListener('mousedown', handler);
        window.removeEventListener('scroll', scrollHandler, true);
      };
    }
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
    } else {
      const rect = btnRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left, width: 208 });
      setOpen(true);
    }
  };

  const select = async (status) => {
    setOpen(false);
    if (status === 'Select Status') status = 'DRAFT';
    if (status === currentStatus || (status === 'DRAFT' && (currentStatus === 'EXTRACTED' || !currentStatus))) return;
    setUpdating(true);
    try {
      await fetch(`${API_BASE_URL}/applications.php?action=update_status&id=${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ 
          status,
          employee_id: employeeId || '',
          task_id: taskId || ''
        }).toString(),
      });
      onChange(customerId, status);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        disabled={updating}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${statusClass(currentStatus)}`}
      >
        {updating ? 'Saving…' : ((currentStatus === 'EXTRACTED' || currentStatus === 'extracted' || currentStatus === 'DRAFT' || currentStatus === 'draft') ? 'Select Status' : (currentStatus || '—'))}
        <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && coords && (
        <div
          ref={menuRef}
          style={{ top: coords.top, left: coords.left, width: coords.width }}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {STATUS_OPTIONS.map(s => (
            <button key={s} onClick={() => select(s)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors flex items-center gap-2 ${s === currentStatus ? 'font-semibold' : 'text-gray-700'}`}>
              <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${statusClass(s).split(' ')[0]}`} />
              {s}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

/* ── Main Page ─────────────────────────────────────────────────────── */
const AddFormPage = () => {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete, canView } = getPermissions('Add Form');
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [modal, setModal]           = useState(null); // null | 'add' | record (edit) | { view: record }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mailTarget, setMailTarget] = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [toast, setToast]           = useState(null);
  const [search, setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loanAmountFilter, setLoanAmountFilter] = useState('');
  const [perPage, setPerPage]           = useState(() => {
    const saved = localStorage.getItem('add_form_per_page');
    return saved ? parseInt(saved, 10) : 10;
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchRecords = useCallback(async (p = 1, q = '', status = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: perPage, source: 'add customer,public' });
      if (q)      params.set('search', q);
      if (status) params.set('status', status);
      if (loanAmountFilter) {
        const [min, max] = loanAmountFilter.split('-');
        if (min) params.set('min_loan', min);
        if (max) params.set('max_loan', max);
      }
      const res = await fetch(`${TABLE_URL}?${params}`);
      const data = await res.json();
      if (data.success) {
        setRecords(data.data || []);
        setPagination(data.pagination || { total: 0, pages: 1 });
      }
    } catch {
      showToast('Failed to load records', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search input; also resets to page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { fetchRecords(page, debouncedSearch, statusFilter); }, [fetchRecords, page, debouncedSearch, statusFilter, loanAmountFilter, perPage]);

  const [descModalTarget, setDescModalTarget] = useState(null);

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  const handleRowStatusChange = (customerId, newStatus) => {
    setRecords(prev => prev.map(c => {
      const id = c.id || c.application_id || c.applicant_id;
      if (id === customerId) {
        if (newStatus !== 'DRAFT') {
          setDescModalTarget({ ...c, status: newStatus });
        }
        return { ...c, status: newStatus };
      }
      return c;
    }));
  };

  const handleDescriptionSaved = (customerId, desc) => {
    setRecords(prev => prev.map(c => {
      const id = c.id || c.application_id || c.applicant_id;
      return id === customerId ? { ...c, status_description: desc } : c;
    }));
    showToast('Description updated successfully');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${TABLE_URL}?id=${deleteTarget.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Delete failed');
      setDeleteTarget(null);
      showToast('Record deleted successfully');
      fetchRecords(page, debouncedSearch, statusFilter);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaved = (message) => {
    showToast(message);
    fetchRecords(page, debouncedSearch, statusFilter);
  };

  const openEdit = async (row) => {
    try {
      const res = await fetch(`${TABLE_URL}?id=${row.application_id}`);
      const data = await res.json();
      if (data.success) setModal(data.data);
      else showToast('Failed to load record', 'error');
    } catch {
      showToast('Failed to load record', 'error');
    }
  };

  const openView = (row) => {
    navigate(`/dashboard/add-form/${row.application_id || row.id || row.applicant_id}`);
  };

  const formatCurrency = (val) =>
    val ? `₹ ${Number(val).toLocaleString('en-IN')}` : '—';

  const formatDate = (val) =>
    val ? new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Loan Applications</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {pagination.total} total record{pagination.total !== 1 ? 's' : ''}
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-sm"
          >
            <Plus size={16} />
            Add New Record
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search anything…"
            className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => handleStatusChange(e.target.value)}
          className="py-2 pl-3 pr-8 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent text-gray-600"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={loanAmountFilter}
          onChange={e => { setLoanAmountFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-primary-500"
        >
          <option value="">Any Amount</option>
          <option value="0-100000">Under ₹1 Lakh</option>
          <option value="100000-500000">₹1 Lakh - ₹5 Lakhs</option>
          <option value="500000-1000000">₹5 Lakhs - ₹10 Lakhs</option>
          <option value="1000000-">Over ₹10 Lakhs</option>
        </select>

        {/* Clear all */}
        {(search || statusFilter || loanAmountFilter) && (
          <button
            onClick={() => { setSearch(''); handleStatusChange(''); setLoanAmountFilter(''); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="dashboard-card">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 size={28} className="animate-spin" />
          </div>
        ) : records.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <Plus size={40} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">No records found</p>
            <p className="text-xs mt-1">Click "Add New Record" to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto min-h-[250px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">#</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Name</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Contact</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap text-center">Mail</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Product Type</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Loan Amt</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Loan OD</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">DSA Name</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Status Description</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Date</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 whitespace-nowrap text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row, i) => (
                  <tr key={row.application_id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400 font-medium">
                      {(page - 1) * perPage + i + 1}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                      {row.client_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.contact_no || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => setMailTarget(row)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex"
                        title="Send Mail"
                      >
                        <Mail size={16} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.product_type || '—'}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                      {formatCurrency(row.required_loan_amount)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.loan_od || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.dsa_name || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusDropdown
                        customerId={row.application_id || row.id}
                        currentStatus={row.status}
                        onChange={handleRowStatusChange}
                        employeeId={sessionManager.getUser()?.id}
                      />
                    </td>
                    <td className="px-4 py-3 min-w-[150px] max-w-[200px]">
                      <div
                        onClick={() => setDescModalTarget(row)}
                        className="cursor-pointer hover:bg-gray-100 p-1.5 rounded transition-colors text-xs text-gray-600 line-clamp-2"
                        title={row.status_description || 'Click to add description'}
                      >
                        {row.status_description || <span className="text-gray-400 italic">No description...</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      {formatDate(row.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        {canView && (
                          <button
                            onClick={() => openView(row)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye size={14} />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => openEdit(row)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => setDeleteTarget({ id: row.application_id, application_id: row.application_id, client_name: row.client_name })}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex flex-wrap items-center justify-between px-4 py-3 border-t border-gray-100 gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Rows per page:</span>
              <div className="flex flex-wrap gap-1">
                {[10, 20, 50, 100, 250, 500].map(n => (
                  <button
                    key={n}
                    onClick={() => { setPerPage(n); setPage(1); localStorage.setItem('add_form_per_page', n); }}
                    className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                      perPage === n
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-xs text-gray-500">
                Page {page} of {pagination.pages} ({pagination.total} records)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center gap-1"
                >
                  <ChevronLeft size={13} /> Prev
                </button>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center gap-1"
                >
                  Next <ChevronRight size={13} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {mailTarget && <MailModal target={mailTarget} onClose={() => setMailTarget(null)} />}
      {descModalTarget && (
        <DescriptionModal
          record={descModalTarget}
          onClose={() => setDescModalTarget(null)}
          onSaved={handleDescriptionSaved}
        />
      )}
      {modal && !modal.view && (
        <FormModal
          record={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
      {modal?.view && (
        <ViewModal
          record={modal.view}
          onClose={() => setModal(null)}
          onEdit={() => setModal(modal.view)}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          record={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

export default AddFormPage;
